import type { FiscalOpenSale, SaleResponse, TicketOfficeView } from '../../../types/ticket-office';
import type { FiscalOpenSaleModalProps } from '../../components/FiscalOpenSaleModal';
import { formatPrice } from '../../number-utils';
import { Api } from '../api';

/**
 * How long a toast stays visible before the page is left or reloaded.
 */
const TOAST_BEFORE_LEAVING_MS = 1500;

/**
 * Show the open sale at the cash register. While `state.fiscalOpen` is set the modal stays up.
 */
export const openFiscalSale = (view: TicketOfficeView, sale: FiscalOpenSale): void => {
    view.state.fiscalOpen = sale;
    view.update();
};

const leave = (redirectUrl?: string, delay = 0): void => {
    window.setTimeout(() => {
        if (redirectUrl) {
            window.location.assign(redirectUrl);
        } else {
            window.location.reload();
        }
    }, delay);
};

/**
 * Settle the open sale through its retry or cancel URL and act on the answer.
 */
const settle = async (view: TicketOfficeView, action: 'retry' | 'cancel'): Promise<void> => {
    const { state } = view;
    const sale = state.fiscalOpen;

    if (!sale || state.fiscalSaleBusyAction) {
        return;
    }

    state.fiscalSaleBusyAction = action;
    view.update();

    let json: SaleResponse;

    try {
        const response = await Api.post(action === 'retry' ? sale.retry_url : sale.cancel_url, state.csrfToken);

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        json = await response.json();
    } catch {
        window.toastr.error(state.lang.errors.submitFailed);
        state.fiscalSaleBusyAction = undefined;
        view.update();
        return;
    }

    if (json.success) {
        // The page is left; the buttons stay disabled until it is.
        state.fiscalOpen = null;
        view.update();

        if (json.canceled) {
            window.toastr.success(state.lang.fiscal.canceled);
            leave(undefined, TOAST_BEFORE_LEAVING_MS);
            return;
        }

        if (json.message) {
            window.toastr.success(json.message);
        }

        leave(json.redirectUrl, json.message ? TOAST_BEFORE_LEAVING_MS : 0);
        return;
    }

    if (json.error) {
        window.toastr.error(json.error);
    }

    state.fiscalSaleBusyAction = undefined;

    if (json.fiscal_open) {
        openFiscalSale(view, json.fiscal_open);
        return;
    }

    // Not an open sale of this cash register (any more): start from a fresh page.
    state.fiscalOpen = null;
    view.update();
    leave(undefined, json.error ? TOAST_BEFORE_LEAVING_MS : 0);
};

export const getFiscalOpenSaleModalProps = (view: TicketOfficeView): FiscalOpenSaleModalProps | undefined => {
    const { state } = view;
    const { lang, currencySymbol } = state;
    const sale = state.fiscalOpen;

    if (!sale) {
        return undefined;
    }

    return {
        title: lang.fiscal.title,
        message: sale.message,
        efrMessage: sale.efr_message ?? undefined,
        efrMessageLabel: lang.fiscal.efrMessage,
        invoiceLabel: lang.fiscal.invoice,
        invoice: sale.invoice,
        totalLabel: lang.fiscal.total,
        total: formatPrice(sale.total, currencySymbol),
        retryLabel: lang.fiscal.retry,
        cancelLabel: lang.fiscal.cancelSale,
        busyLabel: lang.fiscal.busy,
        canRetry: sale.can_retry,
        busyAction: state.fiscalSaleBusyAction,
        onRetry: (): void => {
            settle(view, 'retry');
        },
        onCancel: (): void => {
            settle(view, 'cancel');
        },
    };
};
